import { create } from '@/shared/utils/zustandVue'
import { cloneDeep, fromEntries, isNotNullish } from '@/utils'

import { t } from '../../../i18n/i18n'
import { PAGE_MODE, type PageMode } from '../../../shared/constants'
import { ErrorService } from '../../../shared/service/errorService'
import { SnackbarService } from '../../../shared/snackbar/snackbar.service'
import { StaticConfigService } from '../staticCfg.service'
import { type StaticConfig } from '../staticCfg.types'

class StaticCfgsState {
  mode: PageMode = PAGE_MODE.VIEW
  dict: Record<string, StaticConfig> = {}
  current: StaticConfig | null = null
  loading = false
}

type StaticCfgsStore = StaticCfgsState & {
  load: () => void
  unload: () => void
  setDeleting: (payload: StaticConfig) => void
  setView: () => void
  setItems: (payload: StaticConfig[]) => void
  list: () => Promise<void>
  delete: () => Promise<void>
  duplicate: (item: StaticConfig) => Promise<void>
}

/**
 * Static configs store — idiomatic zustand-vue.
 *
 * Tracks the dictionary of static configs, the current config being deleted,
 * the page mode, and a loading flag. `list` fetches all configs; `delete`
 * removes the current config then refreshes the list; `duplicate` clones a
 * config with a "(copie)" suffix on its label.
 */
export const useStaticCfgsStore = create<StaticCfgsStore>((set, get) => ({
  ...new StaticCfgsState(),
  load: () => {
    set({ loading: true })
  },
  unload: () => {
    set({ loading: false })
  },
  setDeleting: (payload) => {
    set({ mode: PAGE_MODE.DELETE, current: cloneDeep(payload) })
  },
  setView: () => {
    set({ mode: PAGE_MODE.VIEW, current: null, loading: false })
  },
  setItems: (payload) => {
    set({
      dict: fromEntries(
        payload
          .map((item) =>
            item._id != null ? ([item._id, item] as const) : null,
          )
          .filter(isNotNullish),
      ),
      loading: false,
    })
  },
  list: async () => {
    try {
      get().load()
      get().setItems(await StaticConfigService.getAll())
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },
  delete: async () => {
    try {
      get().load()
      const { current } = get()
      if (current?._id == null) return
      await StaticConfigService.delete(current._id)
      SnackbarService.info(t('STATIC_CFGS.DELETE_SUCCESS'))
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      await get().list()
      get().setView()
    }
  },
  duplicate: async (item) => {
    try {
      get().load()
      const { _id, ...woIdItem } = item
      await StaticConfigService.save({
        ...woIdItem,
        label: (item.label ?? '') + ' (copie)',
      })
      get().setItems(await StaticConfigService.getAll())
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },
}))
